import MoreVertIcon from '@mui/icons-material/MoreVert';
import { blue } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { fetchSmartlinkParameterForRelayController } from '@sophocles/entities/SmartlinkParameter/smartlinkParameterActions';
import { selectSmartLinkParametersByRelayControllerId } from '@sophocles/entities/SmartlinkParameter/smartlinkParameterSelectors';
import { loadSiteFeatureAssociations } from '@sophocles/features/featureFlags/api';
import { selectSiteFeatureAssociationByKey } from '@sophocles/features/featureFlags/slice';
import { getQueryParam } from '@sophocles/utils';

const selectedStyle = { color: blue[500] };

function PowerMenu({ site, selectedPage, onMobile }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { t } = useTranslation();

  const _determineStyle = page => (selectedPage == page ? selectedStyle : null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(undefined);
  const { isStaff } = useUser();

  const relay_controller = site?.relay_controller;
  const model = relay_controller?.model;

  const meteringFeature = useSelector(selectSiteFeatureAssociationByKey(site?.id, 'METERING'));
  const meteringEnabled = meteringFeature?.is_enabled ?? false;

  const smartlinkParameters = useSelector(selectSmartLinkParametersByRelayControllerId(relay_controller?.id));

  React.useEffect(() => {
    if (!smartlinkParameters) {
      dispatch(fetchSmartlinkParameterForRelayController(relay_controller?.id));
    }

    if (!meteringFeature) {
      dispatch(loadSiteFeatureAssociations(site?.id));
    }
  }, []);

  const menuClick = (e, url) => {
    e.preventDefault();
    navigate(url);
  };

  if (model == null) {
    return null;
  }

  const measuresPower = relay_controller?.measures_power_consumption_in_watts ?? false;
  const measuresComplexPower = model?.measures_complex_ac_power ?? false;
  const measuresGeneratedPower =
    (model?.measures_generated_power ?? false) || (meteringEnabled && smartlinkParameters?.power_meter_type == 0);
  const hasAnsiDoubleConsumption =
    (model?.has_dual_ansi_consumption_meters ?? false) ||
    (meteringEnabled && smartlinkParameters?.power_meter_type == 1);

  const powerOrCurrent = measuresPower ? t('powerData.power', 'Power') : t('powerData.current', 'Current');

  let dateParams = '';

  const dateStart = getQueryParam(location, 'date_start');
  const dateEnd = getQueryParam(location, 'date_end');
  if (dateEnd && dateStart) {
    dateParams = `?date_end=${dateEnd}&date_start=${dateStart}`;
  }

  const showMenu = measuresComplexPower || measuresGeneratedPower || hasAnsiDoubleConsumption || meteringEnabled;

  return showMenu ? (
    <Fragment>
      <IconButton
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={event => {
          setMenuOpen(true);
          setAnchorEl(event.currentTarget);
        }}
        style={{ padding: onMobile ? '0' : null, width: onMobile ? '0' : null }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClick={event => {
          setMenuOpen(false);
        }}
        onClose={event => {
          setMenuOpen(false);
        }}
      >
        <a
          href="#"
          onClick={e =>
            menuClick(
              e,
              `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings${dateParams}`,
            )
          }
        >
          <MenuItem
            style={_determineStyle('power')}
          >{`${powerOrCurrent} ${t('powerData.readings', 'Readings')}`}</MenuItem>
        </a>

        {measuresComplexPower ? (
          <a
            href="#"
            onClick={e =>
              menuClick(
                e,
                `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power/consumption${dateParams}`,
              )
            }
          >
            <MenuItem style={_determineStyle('consumption')}>
              {t('pages.powerConsumption', 'Power Consumption')}
            </MenuItem>
          </a>
        ) : null}

        {isStaff && measuresComplexPower ? (
          <a
            href="#"
            onClick={e =>
              menuClick(
                e,
                `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power/meter${dateParams}`,
              )
            }
          >
            <MenuItem style={_determineStyle('meter')}>
              {t('sites.internationalPowerMeterAdmin', 'International Power Meter (admin)')}
            </MenuItem>
          </a>
        ) : null}

        {measuresGeneratedPower ? (
          <a
            href="#"
            onClick={e =>
              menuClick(
                e,
                `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power/solar${dateParams}`,
              )
            }
          >
            <MenuItem style={_determineStyle('solar')}>
              {t('pages.solarPowerReadings', 'Solar Power Readings')}
            </MenuItem>
          </a>
        ) : null}

        {hasAnsiDoubleConsumption ? (
          <a
            href="#"
            onClick={e =>
              menuClick(
                e,
                `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}/readings/power/ansi${dateParams}`,
              )
            }
          >
            <MenuItem style={_determineStyle('ansi')}>{t('sites.ansiPowerReadings', 'ANSI Power Readings')}</MenuItem>
          </a>
        ) : null}
      </Menu>
    </Fragment>
  ) : null;
}

export default PowerMenu;
